import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/contexts/LanguageContext";
import { ASSETS } from "@/lib/assets";
import { ArrowRight, Award, GraduationCap, Beaker, Cpu, Lock, Sparkles } from "lucide-react";

export default function Journey() {
  const { tr } = useLang();
  return (
    <div className="min-h-screen flex flex-col">
      <Header transparent />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pfi-dotgrid opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#d4af37]/10 blur-[130px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 pfi-fade-up">
            <div className="pfi-overline">{tr("journey_eyebrow")}</div>
            <h1 className="mt-6 font-serif text-5xl lg:text-7xl leading-[1.02] tracking-tight">
              TUBAGUS <span className="pfi-gradient-text">WIJAYA</span>
            </h1>
            <div className="mt-6 pfi-overline">{tr("journey_role")}</div>
            <p className="mt-8 max-w-xl text-lg text-[#a9a9b8] leading-relaxed font-light">
              "{tr("journey_statement")}"
            </p>
          </div>
          <div className="lg:col-span-5 pfi-fade-up">
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-tr from-[#d4af37]/10 via-transparent to-[#8B5CF6]/10 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden pfi-gold-border bg-[#0e0e12]">
                <img
                  src={ASSETS.founderPortrait}
                  alt="Tubagus Wijaya"
                  data-testid="founder-portrait"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="pfi-overline">{tr("journey_timeline_title")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-5xl">The Journey</h2>
        <div className="mt-12 relative pl-8 sm:pl-12">
          <div className="absolute left-2 sm:left-3 top-0 bottom-0 w-px bg-gradient-to-b from-[#d4af37]/70 via-[#d4af37]/30 to-transparent" />
          {[
            { year: "2012", key: "2012", title: tr("j_2012_title"), body: tr("j_2012_body") },
            { year: "2015", key: "2015", title: tr("j_2015_title"), body: tr("j_2015_body") },
            { year: "2016", key: "2016", title: tr("j_2016_title"), body: tr("j_2016_body") },
            { year: "2016–2018", key: "2016-2018", title: tr("j_16_18_title"), body: tr("j_16_18_body") },
            { year: "2019", key: "2019", title: tr("j_2019_title"), body: tr("j_2019_body") },
            { year: "2022", key: "2022", title: tr("j_2022_title"), body: tr("j_2022_body") },
            { year: "2026", key: "2026", title: tr("j_2026_title"), body: tr("j_2026_body"), highlight: true },
          ].map((n, i) => (
            <div key={i} className="relative mb-12 last:mb-0 pfi-fade-up" data-testid={`journey-node-${n.key}`}>
              <div className={"absolute -left-6 sm:-left-9 top-1.5 h-3 w-3 rounded-full " + (n.highlight ? "bg-[#d4af37] shadow-[0_0_0_6px_rgba(212,175,55,0.15)]" : "bg-[#d4af37]/60")} />
              <div className="pfi-overline">{n.year}</div>
              <h3 className={"mt-2 font-serif text-2xl " + (n.highlight ? "text-[#e6c687]" : "text-[#f8f8fc]")}>{n.title}</h3>
              <p className="mt-3 text-[#a9a9b8] leading-relaxed max-w-2xl">{n.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Perfumery Education */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-overline"><GraduationCap className="inline mr-1" size={12} /> {tr("edu_title")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-4xl">Perfumery Education</h2>
        <div className="mt-10 space-y-3">
          {[
            { title: "Centre of Excellence — Perfumery Diploma", meta: "Distinction · 2018" },
            { title: "The Perfumery Art School UK Ltd — Certificate in Perfumery Art", meta: "2017" },
            { title: "PerfumersWorld — The Art & Technology of Perfumery — Foundation Plus Workshop", meta: "2016" },
            { title: "L'Atelier Parfums & Créations — Perfumery Training", meta: "Bali" },
            { title: "IPTECHS — Founder & Master Perfumer", meta: "Indonesia Perfumer Technology School" },
          ].map((e, i) => (
            <div key={i} className="pfi-card p-5 flex items-start justify-between gap-4">
              <div className="font-serif text-lg text-[#f8f8fc]">{e.title}</div>
              <div className="pfi-overline whitespace-nowrap">{e.meta}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Innovation & Recognition */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-overline"><Award className="inline mr-1" size={12} /> {tr("recog_title")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-4xl">Innovation & Recognition</h2>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {[
            { year: "2015", title: "Industri Kreatif Terbaik", body: "Dinas Pemuda dan Olahraga Jawa Barat" },
            { year: "2015", title: "10 UKM Inovatif Terbaik", body: "Kementerian Koperasi dan UKM" },
            { year: "2016", title: "Wirausaha Baru Terbaik", body: "Parfum Gue · Provinsi Jawa Barat" },
            { year: "2019", title: "Produk Inovatif Terbaik", body: "KADIN Jawa Barat" },
            {
              year: "2019",
              title: "MURI Recognition",
              body: '"Perusahaan Parfum Pertama yang Melayani Pemesanan berdasarkan Karakter Kepribadian Pelanggan"',
            },
            { year: "2022", title: "6 Besar Talenta Wirausaha Berdaya", body: "Bank Syariah Indonesia" },
          ].map((r, i) => (
            <div key={i} className="pfi-card p-5">
              <div className="pfi-overline">{r.year}</div>
              <div className="mt-2 font-serif text-lg text-[#f8f8fc]">{r.title}</div>
              <div className="mt-1 text-sm text-[#a9a9b8]">{r.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Technology Legacy */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-20">
        <div className="pfi-overline">{tr("legacy_title")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-5xl">The Technology Legacy</h2>
        <p className="mt-5 max-w-2xl text-[#a9a9b8]">{tr("legacy_intro")}</p>

        <div className="mt-10 grid md:grid-cols-3 gap-4">
          <LegacyCard logo={ASSETS.twfhLogo} title="TWFH" role={tr("legacy_twfh_role")} bg="#000" contain />
          <LegacyCard logo={ASSETS.iptechsLogo} title="IPTECHS" role={tr("legacy_iptechs_role")} bg="#ffffff" contain />
          <LegacyCard logo={ASSETS.personaLogo} title="PERSONA" role={tr("legacy_persona_role")} bg="#ffffff" contain />
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          <FlowCard icon={<Lock size={16} />} title="PERSONA CORE" role={tr("legacy_core_role")} />
          <FlowCard icon={<Cpu size={16} />} title="PERSONA AI PERFUMER" role={tr("legacy_ai_role")} />
          <FlowCard icon={<Sparkles size={16} />} title="PERSONA FRAGRANCE INTELLIGENCE" role={tr("legacy_platform_role")} highlight />
        </div>
      </section>

      {/* Protected Technology */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-overline">{tr("protected_eyebrow")}</div>
        <h2 className="mt-3 font-serif text-3xl lg:text-4xl">{tr("protected_title")}</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <ProtectedCard title={tr("protected_persona")} desc={tr("protected_persona_desc")} />
          <ProtectedCard title={tr("protected_software")} desc={tr("protected_software_desc")} />
          <ProtectedCard title={tr("protected_core")} desc={tr("protected_core_desc")} highlight />
        </div>
      </section>

      {/* TWFH */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-card overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="p-8 lg:p-12">
              <div className="pfi-overline">{tr("twfh_eyebrow")}</div>
              <h3 className="mt-3 font-serif text-3xl">{tr("twfh_title")}</h3>
              <div className="mt-1 font-serif text-lg text-[#e6c687]">{tr("twfh_subtitle")}</div>
              <div className="mt-3 text-xs font-mono tracking-wider text-[#a9a9b8]">{tr("twfh_position")}</div>
              <p className="mt-6 text-[#a9a9b8] leading-relaxed">{tr("twfh_desc")}</p>
            </div>
            <div className="relative bg-black flex items-center justify-center p-8">
              <img src={ASSETS.twfhLogo} alt="TWFH" className="max-h-[320px] w-auto object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
        <div className="pfi-card p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="pfi-overline">PERSONA FRAGRANCE INTELLIGENCE</div>
            <h3 className="mt-2 font-serif text-2xl lg:text-3xl">Begin your fragrance intelligence journey.</h3>
          </div>
          <Link data-testid="journey-cta-register" to="/register" className="pfi-btn-gold rounded-full px-6 py-3 text-sm inline-flex items-center gap-2">
            Create your account <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function LegacyCard({ logo, title, role, bg = "#0e0e12", contain }) {
  return (
    <div className="pfi-card overflow-hidden">
      <div className="aspect-square flex items-center justify-center" style={{ background: bg }}>
        <img src={logo} alt={title} className={"max-h-[75%] max-w-[75%] " + (contain ? "object-contain" : "object-cover")} />
      </div>
      <div className="p-5">
        <div className="font-serif text-xl">{title}</div>
        <div className="pfi-overline mt-1">{role}</div>
      </div>
    </div>
  );
}

function FlowCard({ icon, title, role, highlight }) {
  return (
    <div className={"pfi-card p-6 " + (highlight ? "border-[#d4af37]/40" : "")}>
      <div className="pfi-overline inline-flex items-center gap-2">{icon}{role}</div>
      <div className={"mt-3 font-serif text-xl " + (highlight ? "text-[#e6c687]" : "")}>{title}</div>
    </div>
  );
}

function ProtectedCard({ title, desc, highlight }) {
  return (
    <div className={"pfi-card p-6 " + (highlight ? "border-[#d4af37]/40" : "")}>
      <div className="pfi-overline">{desc}</div>
      <div className={"mt-3 font-serif text-xl " + (highlight ? "text-[#e6c687]" : "")}>{title}</div>
    </div>
  );
}
